import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Chip,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ShieldIcon from '@mui/icons-material/Shield';
import TimelineIcon from '@mui/icons-material/Timeline';
import CampaignIcon from '@mui/icons-material/Campaign';
import StoreIcon from '@mui/icons-material/Store';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TuneIcon from '@mui/icons-material/Tune';
import BoltIcon from '@mui/icons-material/Bolt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InsightsIcon from '@mui/icons-material/Insights';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { useNavigate } from 'react-router-dom';
import { container } from './styles';

const DashboardScreen: React.FC = () => {
  const navigate = useNavigate();

  const handleGoToAssistant = () => {
    navigate('/home/assistant');
  };

  return (
    <Box sx={container}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 3,
          borderRadius: 3,
          border: theme => `1px solid ${theme.palette.divider}`,
          background:
            'linear-gradient(135deg, rgba(42,87,238,0.08) 0%, rgba(42,87,238,0.02) 100%)',
        }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesomeIcon color="primary" />
              <Typography variant="overline" color="primary">
                Syntraxis AI Control Hub
              </Typography>
            </Stack>
            <Typography variant="h5" mt={1} gutterBottom>
              One workspace for sales, customers, marketing, and reviews
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Syntraxis unifies live POS, ecommerce, finance, marketing, and
              review data. LLM agents turn signals into on-brand campaigns,
              replies, and actions your team can run instantly.
            </Typography>
            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" gap={1}>
              <Chip icon={<StoreIcon />} label="POS: Toast, Square, Shopify" />
              <Chip
                icon={<CampaignIcon />}
                label="Marketing: Meta, Mailchimp"
              />
              <Chip icon={<TimelineIcon />} label="Insights & forecasts" />
              <Chip icon={<ShieldIcon />} label="Multi-tenant isolated" />
            </Stack>
          </Box>
          <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<RocketLaunchIcon />}
              onClick={handleGoToAssistant}>
              Go To AI Assistant
            </Button>
            <Button
              variant="text"
              onClick={() =>
                window.open('https://syntraxis.ai', '_blank', 'noopener')
              }>
              Learn more
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={3} mb={1}>
        {[
          {
            label: 'Sales (7d)',
            value: '+8.4%',
            sub: 'vs prior week across Toast + Square',
            icon: <TrendingUpIcon color="success" />,
          },
          {
            label: 'Avg rating',
            value: '4.3',
            sub: 'Google + Yelp weighted',
            icon: <InsightsIcon color="primary" />,
          },
          {
            label: 'Unreplied reviews',
            value: '6',
            sub: 'Needs reply in AI Assistant',
            icon: <ThumbDownIcon color="error" />,
          },
        ].map(card => (
          <Grid item xs={12} md={4} key={card.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: theme => `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  bgcolor: theme => theme.palette.action.hover,
                  display: 'grid',
                  placeItems: 'center',
                }}>
                {card.icon}
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {card.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {card.sub}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: theme => `1px solid ${theme.palette.divider}`,
            }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <BoltIcon color="primary" />
                <Typography variant="h6">Automations in flight</Typography>
              </Stack>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/home/automations')}>
                Manage
              </Button>
            </Stack>
            <Grid container spacing={2}>
              {[
                {
                  title: 'Review rescue',
                  desc: 'Auto-draft replies when rating < 4.2 and queue for approval.',
                  status: 'Active · 45 replies last week',
                },
                {
                  title: 'Gap-hour booster',
                  desc: 'Triggers social + SMS when 6-9pm sales dip by 10% vs last week.',
                  status: 'Active · 3 campaigns scheduled',
                },
                {
                  title: 'Inventory watch',
                  desc: 'Alerts when SKUs trend down and recommends bundles.',
                  status: 'Paused · needs data sync',
                },
              ].map(card => (
                <Grid item xs={12} md={4} key={card.title}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      height: '100%',
                      borderRadius: 2,
                    }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {card.desc}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="primary"
                      mt={1}
                      display="block">
                      {card.status}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: theme => `1px solid ${theme.palette.divider}`,
            }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <WarningAmberIcon color="warning" />
              <Typography variant="h6">Alerts</Typography>
            </Stack>
            <List dense disablePadding>
              {['Square sync delayed: new receipts pending import'].map(
                item => (
                  <ListItem
                    key={item}
                    disableGutters
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={handleGoToAssistant}>
                        <ArrowForwardIcon fontSize="small" />
                      </IconButton>
                    }>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <WarningAmberIcon color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ variant: 'body2' }}
                      primary={item}
                    />
                  </ListItem>
                ),
              )}
            </List>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" color="text.secondary">
                Critical actions
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="small"
                fullWidth
                onClick={handleGoToAssistant}>
                Reply to 6 negative reviews now
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                fullWidth
                onClick={() => navigate('/home/campaigns')}>
                Pause underperforming campaign
              </Button>
              <Button
                variant="text"
                color="error"
                size="small"
                fullWidth
                onClick={() => navigate('/home/settings')}>
                Review alert thresholds
              </Button>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: theme => `1px solid ${theme.palette.divider}`,
            }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <TuneIcon color="primary" />
              <Typography variant="h6">Customize dashboard</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Change widgets, reorder collections, or pin AI insights by
              default.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/home/settings')}>
                Configure
              </Button>
              <Button variant="text" size="small" onClick={handleGoToAssistant}>
                Ask AI to set up
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export { DashboardScreen };
